import React, { useRef, useState } from 'react';
import PropTypes from 'prop-types';

import {
  Modal,
  ModalContent,
  ModalDescription,
  ModalHeader,
  Cross,
  Popup,
  Button,
  Text,
  Space,
  Conditional,
  InputWithAction,
} from '@cashfree-intl/coherent';
import { CURRENCY_SYMBOL } from 'utils/constants';
import {
  roundOffNumber,
  getItemFromLocalStorage,
  numberWithCommas,
} from 'utils/common';
import CustomIcon from 'components/CustomIcons';
import Formik from 'components/Formik';
import InlineLoader from 'components/InlineLoader';

import {
  MINIMUM_ELIGIBLE_AMOUNT,
  ON_DEMAND_TEST_MID,
  ON_DEMAND_TEST_MINIMUM_ELIGIBLE_AMOUNT,
} from '../constants';

import useClickOutside from '../../../utils/hooks';

import freetrial from '../../../images/free-trial-tag.svg';

const SettleNowModal = ({
  open,
  closeModal,
  limitAmount,
  // remainingLimit,
  // rate,
  handleFormSubmit,
  handleInfoHover,
  isFreeTrialActive,
  getOnDemandSettleNowData,
  unSettledAmountData,
  settlementCategory,
}) => {
  const modalRef = useRef(null);
  useClickOutside(modalRef, closeModal);

  const [settleNowRespObj, setSettleNowRespObj] = useState({});
  const [loading, setIsLoading] = useState(false);

  // adding this to test on demand settlements on mobile app
  const minimumEligibleAmount =
    +getItemFromLocalStorage('merchantId') === ON_DEMAND_TEST_MID
      ? ON_DEMAND_TEST_MINIMUM_ELIGIBLE_AMOUNT
      : MINIMUM_ELIGIBLE_AMOUNT;

  const validate = values => {
    const errors = {};
    if (
      minimumEligibleAmount &&
      values.requestedAmount < minimumEligibleAmount
    ) {
      errors.requestedAmount = `Minimum withdrawal amount is ${
        CURRENCY_SYMBOL.INR
      } ${minimumEligibleAmount}`;
    } else if (limitAmount && values.requestedAmount > limitAmount) {
      errors.requestedAmount = `Maximum withdrawal limit is ${
        CURRENCY_SYMBOL.INR
      } ${limitAmount}`;
    }
    return errors;
  };

  const settleNowObj = {
    settleNowDetails: {
      merchantId: getItemFromLocalStorage('merchantId'),
      requestedAmount: '',
      freeTrialRequest: isFreeTrialActive,
    },
    setIsLoading,
    cb: response => {
      const {
        requestedAmount,
        serviceCharge,
        serviceTax,
        calculatedAmount,
        minimumServiceCharge,
      } = response;
      setSettleNowRespObj({
        requestedAmount,
        serviceCharge,
        serviceTax,
        calculatedAmount,
        minimumServiceCharge,
      });
    },
    settlementType: settlementCategory,
  };

  const showUnsettledAmount =
    unSettledAmountData && unSettledAmountData.unSettledAmount;

  return (
    <Modal className="settle-now-modal" open={open} ref={modalRef}>
      <ModalHeader>
        On-Demand Settlement{' '}
        <Cross
          onClick={closeModal}
          data-event-name="Form_SettleNowModal_Icon_Close"
          data-ignore-inner-text="true"
        />
      </ModalHeader>
      <ModalContent>
        <ModalDescription>
          <Conditional if={isFreeTrialActive}>
            <img src={freetrial} alt="Free Trial Mode" className="mb-1" />
          </Conditional>
          <Space alignItems="center" gap={1}>
            <Text variant="b14" color="bodyLight">
              Withdrawal Limit
            </Text>
            <Text variant="b12" color="bodyLight">
              {isFreeTrialActive
                ? '(For Free Trial)'
                : '(Inclusive of daily limit)'}
            </Text>
            <Conditional if={!isFreeTrialActive}>
              <Popup
                content={
                  <Text variant="b12" color="bodyLight">
                    On-Demand settlement limit is a part of your unsettled
                    balance, capped at your available daily limit.
                    {showUnsettledAmount && (
                      <>
                        <br />
                        <br />
                        Your current unsettled balance is: {
                          CURRENCY_SYMBOL.INR
                        }{' '}
                        {numberWithCommas(
                          roundOffNumber(
                            unSettledAmountData.unSettledAmount || 0,
                          ),
                        ) || 0}
                      </>
                    )}
                  </Text>
                }
                trigger={
                  <span className="pointer">
                    <CustomIcon componentName="INFO" />
                  </span>
                }
                onOpen={() => handleInfoHover('ON_DEMAND_LIMIT_MODAL')}
                position="right center"
              />
            </Conditional>
          </Space>
          <p className="text-dark">
            {CURRENCY_SYMBOL.INR}{' '}
            <span className="w-600">
              {numberWithCommas(roundOffNumber(limitAmount))}
            </span>
          </p>
        </ModalDescription>
        <div className="settle-now-form">
          <Formik
            initialValues={{ requestedAmount: '' }}
            validate={validate}
            onSubmit={handleFormSubmit}
          >
            {({
              values,
              handleFieldBlur,
              handleFieldChange,
              handleSubmit,
              errors,
              touched,
              setFieldValue,
              setFieldTouched,
              ...formikProps
            }) => {
              const disableField =
                !formikProps.isValidating && formikProps.isSubmitting;
              const isEligibleFundsNotAdequate =
                +limitAmount < minimumEligibleAmount;

              const onKeyDown = e => {
                const asciiValue = e.key.charCodeAt(0);

                const arr = String(e.target.value).split('.');
                const decimalValues = arr.length > 1 ? arr.pop() : '';

                if (
                  ['e', 'E', '+', '-'].includes(e.key) ||
                  (asciiValue >= 48 &&
                    asciiValue <= 57 &&
                    decimalValues.length > 1)
                ) {
                  e.preventDefault();
                }
              };

              const computeSettleNow = () => {
                const defaultValue = '0.00';

                if (values.requestedAmount && values.requestedAmount > 0) {
                  const totalCharges =
                    settleNowRespObj.serviceCharge +
                    settleNowRespObj.serviceTax;

                  return {
                    charges:
                      (Number.isNaN(totalCharges)
                        ? defaultValue
                        : roundOffNumber(totalCharges)) || defaultValue,
                    deductedAmount:
                      (Number.isNaN(settleNowRespObj.calculatedAmount)
                        ? defaultValue
                        : roundOffNumber(settleNowRespObj.calculatedAmount)) ||
                      defaultValue,
                  };
                }
                return { charges: defaultValue, deductedAmount: defaultValue };
              };

              return (
                <form autoComplete="off" onSubmit={handleSubmit}>
                  <div className="form-input-wrapper">
                    <div className="field">
                      {/* eslint-disable-next-line jsx-a11y/label-has-associated-control */}
                      <label
                        className={
                          errors && errors.requestedAmount ? 'error-label' : ''
                        }
                      >
                        Amount To Withdraw
                      </label>
                      {/* see if we can limit the numbers after decimal values => refer to eposwebapp */}
                      <InputWithAction
                        fluid
                        id="requestedAmount"
                        name="requestedAmount"
                        value={values.requestedAmount}
                        errors={errors}
                        touched={touched}
                        type="text"
                        label={{ basic: true, content: '₹' }}
                        // allowedValues="numericWithDots"
                        placeholder="0.00"
                        onKeyDown={onKeyDown}
                        onChange={(e, data) => {
                          const regex = /^(0|[1-9]\d*)(\.\d*)?$|^$/;
                          if (!regex.test(e.target.value)) {
                            return;
                          }
                          setFieldTouched('requestedAmount');
                          if (e.target.value) {
                            settleNowObj.settleNowDetails.requestedAmount =
                              e.target.value;
                            getOnDemandSettleNowData({ settleNowObj });
                          }
                          handleFieldChange(e, data);
                        }}
                        onBlur={handleFieldBlur}
                        className="input-with-multiple-labels"
                        disabled={disableField || isEligibleFundsNotAdequate}
                      />

                      <Space direction="column" gap={0.5} className="mb-4 mt-3">
                        <Text variant="b14" color="bodyLight">
                          Amount To Be Deducted (
                          {isFreeTrialActive ? 'Zero' : 'Inclusive of'} add.
                          charges)
                        </Text>
                        <InlineLoader
                          loading={loading}
                          content={
                            <Text variant="h16">
                              {CURRENCY_SYMBOL.INR}{' '}
                              {computeSettleNow().deductedAmount}
                            </Text>
                          }
                        />
                        <Conditional
                          if={
                            !isFreeTrialActive &&
                            settleNowRespObj.minimumServiceCharge &&
                            !loading
                          }
                        >
                          <Text
                            variant="b12"
                            color="placeholder"
                            className="font-normal"
                          >
                            Minimum Additional Charges :{' '}
                            <span className="w-500">{CURRENCY_SYMBOL.INR}</span>{' '}
                            {settleNowRespObj.minimumServiceCharge} (+18% GST)
                          </Text>
                        </Conditional>
                      </Space>
                      <div className="modal-form-cta-wrapper">
                        <Button
                          type="button"
                          link
                          onClick={closeModal}
                          className="link-cta"
                          disabled={disableField || isEligibleFundsNotAdequate}
                          data-event-name="Form_SettleNowModal_Button_Close"
                        >
                          Cancel
                        </Button>
                        <Button
                          primary
                          type="submit"
                          loading={disableField}
                          disabled={
                            !formikProps.isValid ||
                            disableField ||
                            isEligibleFundsNotAdequate
                          }
                          data-event-name="Primary_Button_Confirm"
                        >
                          Confirm
                        </Button>
                      </div>
                    </div>
                  </div>
                </form>
              );
            }}
          </Formik>
        </div>
      </ModalContent>
    </Modal>
  );
};

SettleNowModal.propTypes = {
  open: PropTypes.bool.isRequired,
  closeModal: PropTypes.func.isRequired,
  limitAmount: PropTypes.any,
  handleFormSubmit: PropTypes.func.isRequired,
  handleInfoHover: PropTypes.func.isRequired,
  isFreeTrialActive: PropTypes.bool,
  getOnDemandSettleNowData: PropTypes.func.isRequired,
  unSettledAmountData: PropTypes.any,
  settlementCategory: PropTypes.string,
};

export default SettleNowModal;
